// Shared analysis definitions; values and helpers are maintained here.
import { CHROME, STATUS, CATEGORICAL } from './palette';
import type { Protocol } from './protocols';


// --- Audit detector findings (phase 4) -------------------------------------
// A passive observer can prove misbehaviour but can never prove good
// behaviour — so there are exactly three verdicts, forever. Explicit
// requirement of the phase-4 brief: no fourth "CLEAN"/"healthy" verdict, invented
// or implied. None of these three borrows STATUS.good's colour, on purpose —
// an empty findings list reads as "nothing detected in this window," not
// "all clear," everywhere this is rendered.
export type AuditVerdict = "CONFIRMED" | "SUSPECTED" | "INSUFFICIENT_EVIDENCE";


export const AUDIT_VERDICT_ORDER: AuditVerdict[] = ["CONFIRMED", "SUSPECTED", "INSUFFICIENT_EVIDENCE"];


export interface AuditVerdictInfo {
  label: string;
  color: { light: string; dark: string };
}


// Labels rewritten plain (2026-09-25 audit plain-wording task) — display
// only, the underlying API values (CONFIRMED/SUSPECTED/INSUFFICIENT_
// EVIDENCE) are unchanged everywhere else in this file and the backend.
//
// Colour corrected (2026-09-25, same task, wording/display polish pass):
// this is "how sure are we" (a verdict), never "how bad is it" (a LEVEL —
// see AUDIT_LEVEL_INFO below, which keeps its own real severity colours).
// CONFIRMED/SUSPECTED used to borrow STATUS.critical/warning (red/orange)
// here, which reads as an alarm regardless of what the label says — a
// SUSPECTED-but-INFO-level finding showed an orange "Likely" badge next
// to a grey "Info" one. Neutral, muted-grey tokens only; rendered with
// the outlined `.badge-outline` style (global.css), never the filled
// `.badge` severity treatment.
export const AUDIT_VERDICT_INFO: Record<AuditVerdict, AuditVerdictInfo> = {
  CONFIRMED: { label: "Proven", color: CHROME.inkSecondary },
  SUSPECTED: { label: "Likely", color: CHROME.inkMuted },
  INSUFFICIENT_EVIDENCE: { label: "Not enough data", color: CHROME.inkMuted },
};


// "How sure" tooltips (2026-09-26 check-fixes batch 1, fix 6) — what each
// verdict actually means, read against backend/amalgamesh/audit/finding.py
// (the three-verdict discipline: a passive observer can prove misbehaviour
// but never prove good behaviour). Every detector derives its OWN minimum
// sample size (or, for D5's deterministic registry check, needs no sample
// size at all) — see d1_advert_rate.py's own `min_intervals_for_suspected`
// for the one worked example in code. 2026-09-26 check-fixes batch 2, fix
// 2 ("NOT ENOUGH DATA tooltip number"): this generic tooltip must never
// hard-code any one detector's own derived number (it used to say "about
// 10 observed gaps", which disagreed with whatever a given node's actual
// per-node `min_required` / `advert_min_required` API field said, and is
// wrong for every OTHER detector besides D1 anyway) — the per-node cell
// itself already names the real number (see `formatAdvertSampleBasis`
// and the "not enough adverts yet (n of N)" cell text), so this tooltip
// stays general.
export const AUDIT_VERDICT_TOOLTIP: Record<AuditVerdict, string> = {
  CONFIRMED:
    "The evidence directly proves this — no innocent explanation is possible (or, for a deterministic fact like a shared short ID, there's nothing to infer at all). Rare, since a one-sided listener usually can't rule everything out.",
  SUSPECTED:
    "Matches a suspicious pattern with enough samples to say so with confidence — but a legitimate explanation this tool can't see (e.g. a deliberate, intentional setting) is still possible. Never upgraded to Proven for that reason.",
  INSUFFICIENT_EVIDENCE:
    "Fewer samples were observed than this check's own derived minimum needed to tell a real pattern from noise — each check derives its own minimum from this mesh's own measured capture rate, never a flat number.",
};


export function isAuditVerdict(x: string | null | undefined): x is AuditVerdict {
  return x === "CONFIRMED" || x === "SUSPECTED" || x === "INSUFFICIENT_EVIDENCE";
}


export function auditVerdictInfo(raw: string | null | undefined): AuditVerdictInfo {
  return isAuditVerdict(raw) ? AUDIT_VERDICT_INFO[raw] : AUDIT_VERDICT_INFO.INSUFFICIENT_EVIDENCE;
}


// --- Audit finding LEVEL (2026-09-25 rebuild, check-fixes-design.md §8 /
// amalgamesh-audit-rf-design.md §5, §11 decision 2) ------------------------
// Replaces the old picked high/medium/low/info bucketing (NO SOURCE — see
// backend/amalgamesh/audit/levels.py's own module docstring for the full
// rationale) with two axes collapsed into one flat vocabulary for display:
// an AIRTIME consequence ladder (firmware_limit > airtime_impact >
// against_policy, most to least severe) and a separate PROVEN-FACT track
// (proven_fact / fact) for detectors that aren't airtime matters at all
// (D2 boot-loop, D5 prefix collision, D8 position). `fact` is shared by
// both tracks — either "tier 4, no sourced threshold yet" (airtime) or
// "not yet conclusive" (proven_fact); which track a given finding belongs
// to is carried separately on `AuditFinding.track`, not encoded in the
// level name itself. Mirrors backend/amalgamesh/audit/levels.py's own
// LEVELS tuple exactly, in the same worst-to-least-severe display order.
//
// Colour: firmware_limit/airtime_impact/against_policy reuse this file's
// existing 4-step STATUS palette (critical/serious/warning — the same
// "most to least severe" ordering STATUS already encodes). `proven_fact`
// is deliberately NOT on that ladder — it's a different axis (a confirmed,
// non-airtime fact, not a "how much channel time" severity) — so it gets
// its own already-validated categorical hue (CATEGORICAL.violet) instead
// of borrowing a STATUS severity colour, keeping its badge visually
// distinct from every airtime-tier badge at a glance. `fact` reuses
// CHROME.inkSecondary (a real, already-defined neutral distinct from
// inkMuted's own use for not_enough_data — the two must read as different
// at a glance, not just in name), and `not_enough_data` reuses
// CHROME.inkMuted, the same muted tone AUDIT_VERDICT_INFO.
// INSUFFICIENT_EVIDENCE already uses for the exact same "no real signal
// yet" meaning.
export type AuditLevel = "firmware_limit" | "airtime_impact" | "against_policy" | "proven_fact" | "fact" | "not_enough_data";


export const AUDIT_LEVEL_ORDER: AuditLevel[] = ["firmware_limit", "proven_fact", "airtime_impact", "against_policy", "fact", "not_enough_data"];


export interface AuditLevelInfo {
  label: string;
  color: { light: string; dark: string };
}


export const AUDIT_LEVEL_INFO: Record<AuditLevel, AuditLevelInfo> = {
  firmware_limit: { label: "Firmware limit", color: STATUS.critical },
  airtime_impact: { label: "Airtime impact", color: STATUS.serious },
  against_policy: { label: "Against policy", color: STATUS.warning },
  proven_fact: { label: "Proven fact", color: CATEGORICAL.violet },
  fact: { label: "Fact", color: CHROME.inkSecondary },
  not_enough_data: { label: "Not enough data", color: CHROME.inkMuted },
};


export function isAuditLevel(x: string | null | undefined): x is AuditLevel {
  return (
    x === "firmware_limit" ||
    x === "airtime_impact" ||
    x === "against_policy" ||
    x === "proven_fact" ||
    x === "fact" ||
    x === "not_enough_data"
  );
}


export function auditLevelInfo(raw: string | null | undefined): AuditLevelInfo {
  return isAuditLevel(raw) ? AUDIT_LEVEL_INFO[raw] : AUDIT_LEVEL_INFO.not_enough_data;
}


// A finding whose own text says it is "never a confirmed claim" must not wear
// the "Fact" badge. It gets the same lower-confidence badge every unconfirmed
// signal gets, "Likely". No detector is in this set today.
const UNCONFIRMED_DETECTORS = new Set<string>();


export function findingBadgeInfo(
  level: string | null | undefined, detector: string | null | undefined, verdict: string | null | undefined,
): AuditLevelInfo {
  if (level === "fact" && detector && UNCONFIRMED_DETECTORS.has(detector) && verdict !== "CONFIRMED") {
    return AUDIT_VERDICT_INFO.SUSPECTED;
  }
  return auditLevelInfo(level);
}


// The `track` a level's own detector run landed on — see AuditFinding's
// own `track` field (api/types.ts) and backend/amalgamesh/audit/levels.py's
// module docstring. Informational only today (no UI filter keys on it —
// `level` alone already carries the distinction that matters for
// filtering/display); kept here so a future consumer has one real,
// documented vocabulary to narrow against rather than a bare string.
export type AuditTrack = "airtime" | "proven_fact";


export const AUDIT_TRACK_LABEL: Record<AuditTrack, string> = {
  airtime: "Airtime",
  proven_fact: "Proven fact",
};


export function isAuditTrack(x: string | null | undefined): x is AuditTrack {
  return x === "airtime" || x === "proven_fact";
}


// --- Node traffic-light colour (2026-09-25 simplify rebuild) --------------
// Simplification: the old sections were confusing and buried the useful
// signal. Show the timer, mark it red/yellow/green, and state the number —
// airtime tier, proven facts, and airtime impact are not useful metrics on
// their own. Replaces the Nodes list's old
// worst_level/worst_airtime_tier/proven_fact_count/measured_airtime_impact/
// counts columns with ONE thing: D1's own observed advert interval, colour-
// coded — backend/amalgamesh/audit/nodes.py's COLOR_TOKENS exactly.
// `none` = no D1 colour at all (no data yet, or an interval slower than
// the firmware's 47h new-install-default neutral ceiling — see
// d1_advert_rate.py's own classify_interval_color docstring); rendered
// with no colour at all (`color: null` below), never a 4th real hue.
export type AuditColor = "red" | "yellow" | "green" | "none";


export const AUDIT_COLOR_ORDER: AuditColor[] = ["red", "yellow", "green", "none"];


export interface AuditColorInfo {
  label: string;
  color: { light: string; dark: string } | null;
}


export const AUDIT_COLOR_INFO: Record<AuditColor, AuditColorInfo> = {
  red: { label: "Red", color: STATUS.critical },
  yellow: { label: "Yellow", color: STATUS.warning },
  green: { label: "Green", color: STATUS.good },
  none: { label: "No colour", color: null },
};


export function isAuditColor(x: string | null | undefined): x is AuditColor {
  return x === "red" || x === "yellow" || x === "green" || x === "none";
}


export function auditColorInfo(raw: string | null | undefined): AuditColorInfo {
  return isAuditColor(raw) ? AUDIT_COLOR_INFO[raw] : AUDIT_COLOR_INFO.none;
}


// Default-on-load colour filter (Red+Yellow on by default) — mirrors
// migration 0035's own seeded "Default" builtin view
// filter exactly (backend/amalgamesh/db/migrations/0035_audit_views_
// color_filter.sql).
export const DEFAULT_AUDIT_COLORS: AuditColor[] = ["red", "yellow"];


// --------------------------------------------------------------------------
// Audit table (Analysis > MeshCore > Audit) -- GET /api/audit/table. The
// page's own copy lives here, per the project's copy-lint discipline
// (lint:copy). Column headers, filters, summary bars and every hover
// explanation are built by the pure module lib/auditTable.ts; this block
// holds the page-level labels and the plain-sentence evidence lines shown
// when a row is opened.
// --------------------------------------------------------------------------

export const AUDIT_TABLE_SHOW_ALL_LABEL = "Show all nodes";

export const AUDIT_TABLE_LOCATION_FILTER_LABEL = "Location";

export const AUDIT_TABLE_ROLE_FILTER_LABEL = "Role";

export const AUDIT_TABLE_LOCATION_FILTER_ALL_LABEL = "All locations";

export const AUDIT_TABLE_ROLE_FILTER_ALL_LABEL = "All roles";

export const AUDIT_TABLE_NO_POSITION_LABEL = "No position";

export const AUDIT_TABLE_NO_VALUE_LABEL = "\u2014";

export const AUDIT_TABLE_CLOCK_NOT_REPORTED = "Not reported";

export const AUDIT_TABLE_BARS_HEADING = "Repeaters and room servers";

export const AUDIT_TABLE_CLEAR_FILTERS_LABEL = "Clear filters";

export const AUDIT_TABLE_FILTERS_BUTTON_LABEL = "Filters";

export const AUDIT_TABLE_TOP_SCOPE_PICKER_LABEL = "Scope to count";

export const AUDIT_TABLE_FILTER_REGION_LABEL = "Region Scope";

export const AUDIT_TABLE_FILTER_FOOTPRINT_LABEL = "Footprint";

export const AUDIT_TABLE_FILTER_PATH_LABEL = "Path Hash";

export const AUDIT_TABLE_FILTER_MULTI_LABEL = "Multibyte";

export const AUDIT_TABLE_FILTER_DUPLICATE_LABEL = "Duplicate";

export const AUDIT_TABLE_FILTER_CLOCK_LABEL = "Clock Offset";

export const AUDIT_TABLE_FILTER_ADVERT_LABEL = "Advert Interval";

export const AUDIT_TABLE_FILTER_LOOP_LABEL = "Loop Detection";

export const AUDIT_TABLE_FILTER_PKTS_LABEL = "Pkts/hr";

export const AUDIT_TABLE_HAS_SCOPE_GROUP_LABEL = "Has scope";

export const AUDIT_TABLE_PREV_LABEL = "Prev";

export const AUDIT_TABLE_NEXT_LABEL = "Next";

export const AUDIT_TABLE_SORT_LABEL = "Sort by";

export const AUDIT_TABLE_SORT_DEFAULT_LABEL = "Issues first";

export const AUDIT_TABLE_SORT_ASC_LABEL = "Ascending";

export const AUDIT_TABLE_SORT_DESC_LABEL = "Descending";

export const AUDIT_TABLE_NO_MATCH_LABEL = "No nodes match.";

export const AUDIT_TABLE_NO_SCOPE_LABEL = "No scope";


export function auditTablePagerLine(from: number, to: number, total: number): string {
  return `${from.toLocaleString("en-US")}\u2013${to.toLocaleString("en-US")} of ${total.toLocaleString("en-US")}`;
}


export function auditTableSegmentChipLabel(barLabel: string, stateLabel: string): string {
  return `${barLabel}: ${stateLabel}`;
}


export const AUDIT_SCOPE_STALE_LABEL = "Stale";


export function auditTableScopePillTip(tag: string, name: string | null, confirmed: boolean, staleLabel: string | null = null): string {
  const who = name ? `${tag} (${name})` : tag;
  const base = confirmed
    ? `${who} (confirmed): this node reports this scope, or sent a scoped packet with it.`
    : `${who} (inferred): this node was only seen relaying packets with this scope.`;
  return staleLabel ? `${base} ${staleLabel}` : base;
}


export const AUDIT_TABLE_NO_SCOPE_PILL_TIP =
  "This node reports no real scope, or it was active in this window and every packet it sent or relayed was unscoped.";


// --- Evidence sentences (shown when a row is opened) ---
export function auditTableConfirmedScopeLine(scope: { tag: string; name: string | null }, staleLabel: string | null = null): string {
  const label = scope.name ? `${scope.tag} (${scope.name})` : scope.tag;
  const line = `${label} (confirmed): the node reports this scope, or sent a packet with it.`;
  return staleLabel ? `${line} ${staleLabel}` : line;
}


export function auditTableInferredScopeLine(scope: { tag: string; name: string | null }): string {
  const label = scope.name ? `${scope.tag} (${scope.name})` : scope.tag;
  return `${label} (inferred): only seen relaying packets with this scope.`;
}


export function auditTableNoScopeSentence(unscopedPackets: number): string {
  const word = unscopedPackets === 1 ? "packet" : "packets";
  return `${unscopedPackets === 0 ? "Reports no real scope." : `Reports no scope; active in this window with ${unscopedPackets} unscoped ${word} and none with a scope.`}`;
}


export function auditTableScopeUnknownEvidenceSentence(): string {
  return "No scope information for this node in this window.";
}


export function auditTableGeoRegionEvidenceSentence(regionName: string | null, hasPosition = false): string {
  if (regionName) return `Verified location: ${regionName}.`;
  return hasPosition
    ? "Has a reported position outside every tracked region."
    : "No verified location -- no usable reported position, and not enough nearby neighbours to infer one.";
}


export function auditTablePositionDisagreementIntro(): string {
  return "Observers disagree about its reported position:";
}


export function auditTablePositionDisagreementPart(observerLabel: string, lat: number, lon: number): string {
  return `${observerLabel} reported ${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}


export function auditTableDuplicatePrefixSentence(widthBytes: number, prefix: string, otherCount: number): string {
  const byteWord = widthBytes === 1 ? "1-byte" : `${widthBytes}-byte`;
  const repeaterWord = otherCount === 1 ? "repeater" : "repeaters";
  return `Shares its ${byteWord} prefix (${prefix}) with ${otherCount} other ${repeaterWord}:`;
}


export function auditTableDuplicateAdjacencyConfirmedSentence(): string {
  return "A relay was seen next to this node and another node with the same ID in this window.";
}


export function auditPathHashWidthLabel(width: number): string {
  return width === 1 ? "1 byte" : `${width} bytes`;
}


export function auditTablePathHashEvidenceSentence(
  widthLabel: string, lookbackDays: number, proof: "advert" | "relayed" | null,
): string {
  const base = `Newest own advert in the last ${lookbackDays} days used ${widthLabel}.`;
  if (proof === "advert") return `${base} It has advertised a path hash of 2 bytes or more, so its firmware can do it.`;
  if (proof === "relayed") return `${base} It was seen relaying in a multi-byte path, so its firmware can do it.`;
  return base;
}


export function auditTablePathHashChangedSentence(sequence: string, when: string): string {
  return `Changed width ${sequence} in this window; last change ${when}.`;
}


export function auditTablePathHashChangedTip(sequence: string, when: string): string {
  return `Width ${sequence} bytes, last change ${when}`;
}


export function auditTableDuplicateNoneSentence(width: number): string {
  return `No other repeater or room server shares its ID at ${width} byte${width === 1 ? "" : "s"}.`;
}


export const AUDIT_TABLE_HEARD_TOGETHER_LABEL = "Heard together";

export const AUDIT_TABLE_HEARD_TOGETHER_TIP =
  "Whether any observer heard both this node and the other one in this window. A dash means one of them was not heard by any observer, so there is nothing to compare.";


export function auditTableLoopEvidenceSentence(setting: string, priors: Record<string, number>): string {
  const parts = Object.entries(priors).map(([w, n]) => `${w}-byte: up to ${n} earlier`).join(", ");
  if (setting === "unknown") return "Never seen relaying in this window, so nothing to judge from.";
  return parts
    ? `Strictest setting still consistent with what we saw: ${setting}. It forwarded packets already carrying its own ID (${parts}).`
    : `Strictest setting still consistent with what we saw: ${setting}. No packet it forwarded already carried its own ID.`;
}


export function auditTableRelianceEvidenceSentence(choke: number, through: number, pct: number | null): string {
  if (pct === null) return "not enough packets";
  return `Of the ${through.toLocaleString("en-US")} packets that went through this repeater and were heard by 2+ observers, ${choke.toLocaleString("en-US")} had no path around it — every observer heard them via this repeater.`;
}


export function auditTableWindowProseLabel(window: string): string {
  if (window === "24h") return "24 hours";
  if (window === "7d") return "7 days";
  return window;
}


export const AUDIT_TABLE_CLOCK_NOT_SET_TIP = "The radio's clock was never set. It still shows the default time the radio starts from, so it is more than a year away from real time.";


export function auditTableClockStepTip(level: number): string {
  return `28 h step x ${level}`;
}


export function auditTableClockOffsetEvidenceSentence(offsetLabel: string, driftSPerDay: number | null, stepLevel: number | null = null): string {
  const stepText = stepLevel != null && stepLevel >= 1 ? ` Clock is ${stepLevel} x 28 h ahead (firmware step).` : "";
  if (driftSPerDay == null) return `Currently ${offsetLabel} -- drift not measurable.${stepText}`;
  const absPerDay = Math.abs(driftSPerDay);
  const direction = driftSPerDay >= 0 ? "gaining" : "losing";
  const driftText = absPerDay < 60 ? `${absPerDay.toFixed(1)} s/day` : `${(absPerDay / 60).toFixed(1)} min/day`;
  return `Currently ${offsetLabel}, ${direction} about ${driftText}.${stepText}`;
}


/** A recent clock step as listed in "Recent readings": the 28 h firmware steps by name, any other step as a clock set. */
export function auditTableClockStepLabel(kind: string): string {
  if (kind === "step_28h") return "28 h step";
  if (kind === "step_28h_back") return "28 h step back";
  return "clock set";
}


export function auditTableClockHistorySentence(parts: string[]): string {
  return `Recent readings: ${parts.join(", ")}.`;
}


export function auditTableAdvertHistorySentence(parts: string[]): string {
  return `Recent intervals: ${parts.join(", ")}.`;
}


export function auditTablePktsPerHrEvidenceSentence(valueLabel: string, windowProse: string): string {
  return `${valueLabel}, averaged over the ${windowProse} window.`;
}


// Pkts/hr also counts channel (group) messages matched to this node by
// exact sender name (audit_table.py's `_load_channel_message_matches`) --
// shown only when at least one matched, with the per-channel breakdown and
// an explicit caveat that the sender name itself is never verified.
export function auditTablePktsPerHrChannelMessagesSentence(
  totalMatched: number, byChannel: Array<{ channel: string; count: number }>,
): string {
  const msgWord = totalMatched === 1 ? "message" : "messages";
  const parts = byChannel.map((c) => `${c.channel} ${c.count}`).join(", ");
  return `Includes ${totalMatched} channel ${msgWord} matched by sender name: ${parts}.`;
}


export function auditTablePktsPerHrChannelMatchCaveat(): string {
  return "Channel messages are matched by the name at the start of the message, which isn't verified.";
}


// Advert interval display: hours, or minutes if under 1h.
// `hours` is always the raw numeric value the backend sends
// (evidence.interval_hours / AuditNodeEntry.advert_interval_hours) — never
// pre-formatted server-side, so this is the one place that turns it into
// text.
export function formatAdvertInterval(hours: number): string {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toFixed(1)} ${rounded === 1 ? "hour" : "hours"}`;
}


// --- Advert-interval "no colour" reasons (2026-09-26 check-fixes batch 1,
// fix 4, "NO-COLOUR COUNT UP FRONT") ----------------------------------------
// Mirrors backend/amalgamesh/audit/detectors/d1_advert_rate.py's own
// NO_COLOUR_REASON_* constants exactly — four previously-indistinguishable
// "no colour" situations, now told apart in the Node list.
export type AdvertNoColourReason = "no_timer" | "insufficient_samples" | "over_ceiling" | "no_adverts";


export function isAdvertNoColourReason(x: string | null | undefined): x is AdvertNoColourReason {
  return x === "no_timer" || x === "insufficient_samples" || x === "over_ceiling" || x === "no_adverts";
}


// Roles with NO confirmed periodic flood-advert timer at all (2026-09-26
// check-fixes batch 1, "COMPANIONS NEUTRAL") — mirrors backend/amalgamesh/
// audit/detectors/d1_advert_rate.py's own NEUTRAL_ADVERT_ROLES exactly.
// Firmware-source survey (MeshCore HEAD e94125987ed87497e706a0b54d1e80c7
// 09343980, same commit D1's own firmware-fact citations use):
//   - companion (examples/companion_radio/MyMesh.cpp): no
//     flood_advert_interval field, no next_flood_advert scheduling
//     anywhere in the file — the ONLY way a companion radio sends a flood
//     advert is the connected app issuing CMD_SEND_SELF_ADVERT (line
//     1250), entirely on the app's own initiative, never a device timer.
//   - observer: not a MeshCore firmware role at all — Amalgamesh's own
//     fallback label (backend/amalgamesh/observers/corescope.py) for a
//     CoreScope-listed receiving/listening station, not a mesh device.
//   - sensor (examples/simple_sensor/SensorMesh.cpp) is DELIBERATELY NOT
//     in this set — it has the same flood_advert_interval field/timer
//     repeater/room_server use (default disabled, but real), confirmed
//     against the same firmware commit — it keeps its colour.
export const NEUTRAL_ADVERT_ROLES: readonly string[] = ["companion", "observer"];


export function isNeutralAdvertRole(role: string | null | undefined): boolean {
  return !!role && NEUTRAL_ADVERT_ROLES.includes(role);
}


// Plain label for each no-colour reason — role-aware for "no_timer" (says
// which neutral role, per this task's own wording: "no timer — companion").
// `neutralCeilingHours` is the live Settings value (AuditNodesResponse.
// advert_color_settings.neutral_ceiling_hours, 47h by default) — never a
// hard-coded "47 h", same discipline as the Advert-interval column's own
// header tooltip.
export function advertNoColourReasonLabel(
  reason: AdvertNoColourReason,
  role: string | null | undefined,
  neutralCeilingHours?: number,
): string {
  switch (reason) {
    case "no_timer":
      return `no timer — ${role ? auditRoleLabel(role).toLowerCase() : "no confirmed timer"}`;
    case "insufficient_samples":
      return "not enough adverts yet";
    case "over_ceiling":
      return neutralCeilingHours != null ? `over ${Math.round(neutralCeilingHours)} h` : "over the neutral ceiling";
    case "no_adverts":
      return "no adverts heard";
    default:
      return "no colour";
  }
}


export const ADVERT_NO_COLOUR_REASON_TOOLTIP: Record<AdvertNoColourReason, string> = {
  no_timer:
    "This role has no confirmed periodic flood-advert timer in MeshCore firmware — a companion only sends a flood advert when the connected app asks; an observer is Amalgamesh's own label for a listening station, not a mesh device with a timer. There's nothing to measure a rate against.",
  insufficient_samples: "Fewer flood-advert gaps were observed than this check's own derived minimum needed to tell a real rate from noise.",
  over_ceiling: "A real interval was measured, just slower than this mesh's neutral ceiling — not a violation, just not colour-coded.",
  no_adverts: "No flood-advert data at all for this node — it appears here only because another check flagged it.",
};


// "from N gaps over X days" (2026-09-26 check-fixes batch 1, fix 3,
// "SAMPLE BASIS PER ROW") — `days` is always the backend's own
// AuditNodesResponse.advert_lookback_days (the SAME lookback the colour
// column actually queried, never a hard-coded "6 days" — see backend/
// amalgamesh/audit/nodes.py's own comment).
export function formatAdvertSampleBasis(sampleCount: number, days: number): string {
  const roundedDays = Math.round(days * 10) / 10;
  return `from ${sampleCount} gap${sampleCount === 1 ? "" : "s"} over ${roundedDays} day${roundedDays === 1 ? "" : "s"}`;
}


// 2026-09-26 check-fixes batch 2, fix 3 ("NO-ADVERTS COMPANION shows
// dash"): the plain-text line for a node with literally zero flood-advert
// data at all — a `no_timer` role (companion/observer) that has never
// sent even one app-triggered advert, or a `no_adverts` node (D1 never
// saw this node at all, appeared here only via another check). Neither
// case is a real "no data available" dash — it's a fact ("this node has
// not sent a flood advert"), so it gets the same plain grey text either
// way instead of an uninformative em dash.
export const NO_FLOOD_ADVERTS_TEXT = "no flood adverts heard";


// The neutral grey line for a "no_timer" node (companion/observer) — "N
// flood adverts from the app" (always app-triggered, never a device
// timer — see NEUTRAL_ADVERT_ROLES' own citation) plus "last gap X" when
// there's a gap to show (this task's own requirement 1). Zero events
// (2026-09-26 check-fixes batch 2, fix 3) reads as `NO_FLOOD_ADVERTS_TEXT`
// instead of the literal, confusing "0 flood adverts from the app".
export function formatNeutralAdvertLine(eventCount: number, lastGapHours: number | null | undefined): string {
  if (eventCount === 0) return NO_FLOOD_ADVERTS_TEXT;
  const base = `${eventCount} flood advert${eventCount === 1 ? "" : "s"} from the app`;
  if (lastGapHours == null) return base;
  return `${base}, last gap ${formatAdvertInterval(lastGapHours)}`;
}


// "+N manual adverts" (2026-09-26 check-fixes batch 1, fix 2, "EXTRA
// ADVERTS") — REPEATER/ROOM_SERVER only, backend/amalgamesh/audit/
// detectors/d1_advert_rate.py's own `_apply_role_floor`/`extra_adverts`.
export function formatExtraAdvertsNote(count: number): string {
  return `+${count} manual advert${count === 1 ? "" : "s"}`;
}


export const EXTRA_ADVERTS_TOOLTIP =
  "Flood adverts sent between timer beats by the advert command, at the node or by remote admin (MeshCore CommonCLI.cpp:195-198). Not counted in the interval.";


// Detector labels are authoritative from the backend itself (GET
// /api/audit/detectors' own `label` field, and each finding's own
// `detector_label`) — the API is the source of truth for the live detector
// roster, which can grow over time. This is only a display fallback for the
// rare case a label comes back empty, keyed by detector id, so a known
// detector still reads as a name instead of a raw id.
// Kept in sync with the backend registry's own DETECTOR_LABEL constants
// (backend/amalgamesh/audit/detectors/*.py, 2026-09-25 plain-wording
// task) — a display fallback only, never the primary source.
export const AUDIT_DETECTOR_LABEL_FALLBACK: Record<string, string> = {
  d1_advert_rate: "Advertising too often",
  d2_boot_loop: "Clock resets",
  d3_loop_detect: "Loop Detection off",
  d4_path_hash_width: "1-byte path hash",
  d5_prefix_collision: "Nodes sharing a short ID",
  d8_position_plausibility: "Impossible location",
};


export function detectorLabel(id: string, apiLabel?: string | null): string {
  if (apiLabel) return apiLabel;
  if (AUDIT_DETECTOR_LABEL_FALLBACK[id]) return AUDIT_DETECTOR_LABEL_FALLBACK[id];
  return id.replace(/^d\d+_/, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}


// One-line explanations for an Issues-column chip (2026-09-26 check-fixes
// batch 1, fix 6) — richer than the backend's own one-line DETECTOR_
// MEANING (GET /api/audit/detectors' `meaning` field, AuditDetector.meaning)
// for the two detectors this task called out by name; every other
// detector's chip still falls back to that backend-authoritative meaning
// (see IssueDots in BehaviourTab.tsx), never a raw id.
export const AUDIT_ISSUE_TOOLTIP: Record<string, string> = {
  d5_prefix_collision:
    "The short ID is a 1, 2, or 3-byte prefix of a node's public key — MeshCore's own compact way of naming a node in a path hash. This flags two or more known nodes that happen to share the exact same prefix.",
};


// Formats a duration given in seconds as whole minutes when it divides
// evenly, otherwise minutes+seconds — used only by clockOffsetIssueTooltip
// below, so the interpolated boundary always reads like a duration
// ("1 min", "13 min", "90 sec") rather than a raw seconds count.
function formatSecondsAsDuration(s: number): string {
  if (s < 60) return `${s} sec`;
  const minutes = s / 60;
  return Number.isInteger(minutes) ? `${minutes} min` : `${(s / 60).toFixed(1)} min`;
}


// clock_offset is a synthetic issue (backend/amalgamesh/audit/nodes.py),
// not a real detector: shown only when this node's own clock offset is
// yellow or red (per the configured policy, audit.clock_yellow_from_s/
// clock_red_from_s) — never for green. Settings-coverage pass (part 4,
// 2026-09-27): the boundary numbers this tooltip quotes are now always the
// LIVE configured values (GET /api/audit/nodes's own clock_color_settings,
// same values the traffic-light colour itself was computed from — see
// audit/clock.py), never the hard-coded "1 min"/"13 min" defaults this
// tooltip used to always print regardless of what Settings actually held.
function clockOffsetIssueTooltip(clockColorSettings?: { yellow_from_s: number; red_from_s: number }): string {
  const yellow = clockColorSettings ? formatSecondsAsDuration(clockColorSettings.yellow_from_s) : "1 min";
  const red = clockColorSettings ? formatSecondsAsDuration(clockColorSettings.red_from_s) : "13 min";
  return (
    `This node's own advert clock disagrees with trusted receipt time by more than the clock-offset limits set in Settings ` +
    `(currently: under ${yellow} fine, ${yellow}-${red} caution, ${red} or more a problem). Behind counts the same as ahead.`
  );
}


// Looks up a chip's tooltip by detector id — AUDIT_ISSUE_TOOLTIP's static
// entries, plus clock_offset's own live-interpolated one (not stored in
// that map since it needs the caller's current clockColorSettings).
export function auditIssueTooltip(detectorId: string, clockColorSettings?: { yellow_from_s: number; red_from_s: number }): string | undefined {
  if (detectorId === "clock_offset") return clockOffsetIssueTooltip(clockColorSettings);
  return AUDIT_ISSUE_TOOLTIP[detectorId];
}


// --- Audit node ROLE (Behaviour tab, 2026-09-25) ---------------------------
// Deliberately separate from MESHCORE_ROLE_ORDER/MESHTASTIC_ROLE_ORDER
// above (the map-marker colour categories): those are NORMALIZED display
// buckets with an "other" catch-all (normalizeMeshtasticRole/
// normalizeMeshCoreRole fold e.g. raw "CLIENT_HIDDEN" into "other", and
// lowercase everything). A role FILTER has to send the backend the exact
// literal string stored in `nodes.role` / finding `subject.role` (audit/
// filters.py's `_matches_role` does a plain `==`, no normalization) — so
// this is its own, unnormalized vocabulary, in two casings because the
// two real sources store two different casings:
//
// MeshCore (lowercase, this platform's own vocabulary): backend/
// amalgamesh/ingest/normalize_meshcore.py maps a contact's own AdvType
// byte, and observers/corescope.py maps CoreScope's own role string, onto
// this same four-value set.
//
// Meshtastic (uppercase, stored verbatim): backend/amalgamesh/ingest/
// normalize.py's normalize_meshtastic_node already stores the python
// `meshtastic` library's own node dict `user.role` untouched — this list
// mirrors that library's protobuf enum (meshtastic.protobuf.config_pb2.
// Config.DeviceConfig.Role) in enum order, confirmed against the exact
// version this backend depends on. ROUTER_CLIENT (enum value 3) is
// deliberately excluded — marked `deprecated = true` in the .proto itself.
// "observer" added 2026-09-25 — a real, stored MeshCore role (backend/
// amalgamesh/observers/corescope.py's own fallback for a CoreScope
// registered observer node with no other recognized role), confirmed
// live against that module's own role-mapping table. Previously missing
// from this list even though the value itself was already real data.
export const AUDIT_MESHCORE_ROLE_ORDER = ["companion", "repeater", "room_server", "sensor", "observer"] as const;


export const AUDIT_MESHTASTIC_ROLE_ORDER = [
  "CLIENT",
  "CLIENT_MUTE",
  "ROUTER",
  "REPEATER",
  "TRACKER",
  "SENSOR",
  "TAK",
  "CLIENT_HIDDEN",
  "LOST_AND_FOUND",
  "TAK_TRACKER",
  "ROUTER_LATE",
  "CLIENT_BASE",
] as const;


// A synthetic filter value — never a real stored role — that matches a
// node/finding with no role on record at all. Mirrors backend/amalgamesh/
// audit/filters.py's own `_UNKNOWN_ROLE` constant exactly.
export const UNKNOWN_ROLE = "unknown";


// Short-ID resolution (2026-09-25, backend audit/run.py's own
// _resolve_short_id_subjects): some MeshCore findings' subject is a 1-3
// byte hash prefix, not a full node identity. `role: "unresolved"` is a
// real, distinct value from the plain "unknown" role filter (a genuinely
// unknown-role but otherwise fully-identified node) — its own synthetic
// filter value, same pattern as UNKNOWN_ROLE, appended only on the
// MeshCore side (Meshtastic findings never carry a short-id subject).
export const UNRESOLVED_ROLE = "unresolved";


export const AUDIT_ROLE_ORDER: string[] = [...AUDIT_MESHCORE_ROLE_ORDER, ...AUDIT_MESHTASTIC_ROLE_ORDER, UNKNOWN_ROLE, UNRESOLVED_ROLE];


// MeshCore's own "repeater"/"sensor" and Meshtastic's own "REPEATER"/
// "SENSOR" are different, real, independently-stored values (case is
// significant) that used to read the same once title-cased, which is why
// the two Meshtastic entries used to carry a "(Meshtastic)" disambiguator
// suffix. The Role filter is protocol-scoped now (auditRolesForProtocol,
// 2026-09-25 protocol split) — only one side's role list is ever shown at
// once, so the two can never collide on screen and the suffix is gone.
// Plain sentence case (2026-09-25 audit plain-wording task): "Room
// server"/"Client mute"/"Router late", not "Room Server"/"Client Mute"/
// "Router Late" — only the first word (and real proper nouns/acronyms:
// TAK) capitalized.
export const AUDIT_ROLE_LABEL: Record<string, string> = {
  companion: "Companion",
  repeater: "Repeater",
  room_server: "Room server",
  sensor: "Sensor",
  observer: "Observer",
  CLIENT: "Client",
  CLIENT_MUTE: "Client mute",
  ROUTER: "Router",
  REPEATER: "Repeater",
  TRACKER: "Tracker",
  SENSOR: "Sensor",
  TAK: "TAK",
  CLIENT_HIDDEN: "Client hidden",
  LOST_AND_FOUND: "Lost and found",
  TAK_TRACKER: "TAK tracker",
  ROUTER_LATE: "Router late",
  CLIENT_BASE: "Client base",
  [UNKNOWN_ROLE]: "Unknown",
  [UNRESOLVED_ROLE]: "Unresolved ID",
};


// One place for the short-id-resolution UI strings, so every surface that
// renders a resolved/unresolved subject (currently just the Behaviour
// tab's Nodes list) says the same thing.
export const SHORT_ID_MATCHED_TAG = "matched by short ID";

export const SHORT_ID_NO_CANDIDATES_TEXT = "No known node has this ID";


// Which role list applies to which protocol (Behaviour tab's own Role
// filter, protocol-scoped 2026-09-25) — MeshCore: Companion/Repeater/Room
// server/Sensor/Observer/Unknown; Meshtastic: its own enum roles/Unknown.
// UNKNOWN_ROLE is appended by the caller, not baked in here, since it's
// rendered as its own always-last checkbox in both lists.
export function auditRolesForProtocol(protocol: Protocol): readonly string[] {
  return protocol === "meshcore" ? AUDIT_MESHCORE_ROLE_ORDER : AUDIT_MESHTASTIC_ROLE_ORDER;
}


export function auditRoleLabel(role: string): string {
  return AUDIT_ROLE_LABEL[role] ?? role;
}


// --- Audit tabs, split by protocol (2026-09-25) ----------------------------
// Replaces the old single AUDIT_SUBMENUS list (byte_hash/behaviour/
// reachability/regions/meshcore_config), shown regardless of protocol, with
// a top-level MeshCore | Meshtastic switch above the tabs (AuditPage.tsx).
// "Byte hash" is retired as its own tab — D4/D5 (its two detectors, both
// MeshCore-only) stay reachable as ordinary entries in the Behaviour tab's
// own Check filter (BehaviourTab.tsx lists every detector GET /api/audit/
// detectors returns whose protocols include the active side, not just the
// five originally mapped to "Behaviour"). "MeshCore config" is renamed
// "Config" (it was already MeshCore-only, so the old name's protocol
// prefix was redundant once the page itself is protocol-split). "Regions"
// has no detector — see RegionsSubmenuPanel in AuditPage.tsx — and is kept
// as a tab on BOTH sides, same component either way.
export type AuditTabId = "audit" | "behaviour" | "reachability" | "regions" | "rf_health" | "paths" | "path_inspector" | "talkers" | "backbone" | "neighbour_graph" | "distance" | "signal" | "location";


export interface AuditTabDef {
  id: AuditTabId;
  label: string;
}


export const AUDIT_TABS_BY_PROTOCOL: Record<Protocol, AuditTabDef[]> = {
  meshcore: [
    // Audit table (2026-09-30) -- GET /api/audit/table, MeshCore only
    // (repeaters/room servers/companions -- no Meshtastic equivalent
    // yet). First in the list per the owner's own instruction -- this is
    // meant to be the first thing an operator sees.
    { id: "audit", label: "Audit" },
    // Behavior, Config and Regions are retired on the MeshCore side
    // (2026-10-04): their findings and numbers now live in the Audit table
    // and on each node's own page (its Audit section). Reachability is retired
    // here too (2026-10-07): the node page's Reach block replaces it. A saved
    // view or link that points at it falls back to Audit (AuditPage.tsx).
    // RF Health (2026-10-04) — replaces the old Airtime tab; MeshCore only
    // (GET /api/rf-health/meshcore, components/RfHealthTab.tsx).
    { id: "rf_health", label: "RF Health" },
    // Paths (2026-10-05) — the most common routes and the repeaters whose IDs
    // loop; MeshCore only (GET /api/paths/meshcore, components/PathsTab.tsx).
    { id: "paths", label: "Paths" },
    // Path Inspector (2026-10-20) -- look up one path of hop prefixes, or two nodes, against the routes we have heard; MeshCore only
    // (GET /api/paths/inspect, components/PathInspectorTab.tsx).
    { id: "path_inspector", label: "Path Inspector" },
    // Top Talkers (2026-10-19) -- the nodes that sent the most packets and the most adverts; MeshCore only
    // (GET /api/audit/talkers, components/TalkersTab.tsx).
    { id: "talkers", label: "Top Talkers" },
    // Backbone (2026-09-30) — GET /api/audit/backbone, MeshCore only (the
    // stable-link graph it's built from is a MeshCore repeater/room_server
    // concept; no Meshtastic equivalent).
    // The id "backbone" is historical: the tab is called Topology now (2026-10-06), the id and route are unchanged.
    { id: "backbone", label: "Topology" },
    // Neighbour Graph (2026-10-20) -- the nodes and their observed direct links as a graph; MeshCore only
    // (GET /api/audit/neighbour-graph, components/NeighbourGraphTab.tsx).
    { id: "neighbour_graph", label: "Neighbour Graph" },
    // Distance and Signal (2026-10-07) — own tabs, read the same backbone snapshot payload.
    { id: "distance", label: "Distance" },
    { id: "signal", label: "Signal" },
    // Impossible Location (2026-10-11) -- one definition for both protocols (GET /api/audit/location-check).
    { id: "location", label: "Impossible Location" },
  ],
  meshtastic: [
    { id: "behaviour", label: "Behavior" },
    { id: "reachability", label: "Reachability" },
    { id: "regions", label: "Regions" },
    { id: "location", label: "Impossible Location" },
  ],
};


// 2026-09-30 layout task: each tab's own former on-page intro/description
// box, moved to a hover tooltip (the `title` attribute) on that tab's own
// sub-tab button instead — see AuditPage.tsx's own tab row and each tab's
// own comment on what used to render this text inline. Only tabs that
// actually had an intro box get an entry here; Behaviour, Config, and
// RF Health never had one.
//
// 2026-09-30 follow-up: Regions has no OTHER content once its old
// tab-level heading is gone — this note IS the whole tab body, not an
// intro to something else below it, so (unlike Reachability's own intro,
// which really was just a preamble) it stays visible in the body too,
// via REGIONS_TAB_NOTE below, in addition to living here as the tab
// button's own tooltip. One string, used both places.
export const REGIONS_TAB_NOTE = "Region-scoped audit findings aren't available yet.";


export const AUDIT_TAB_TOOLTIP: Partial<Record<AuditTabId, string>> = {
  audit:
    "One row per MeshCore node -- region scopes, path hash, duplicate IDs, clock offset, advert interval, loop detection, packets per hour and reliance, colour-coded where something looks off.",
  paths:
    "The most common routes packets take (runs of 2 to 8 repeaters) and which repeaters pass a packet along that already passed it. Each packet counts once, on the longest path any observer heard.",
  path_inspector:
    "Enter a path of hop prefixes, or two nodes, and see the nodes each hop could be and the routes we have actually heard, with how often. A hop that matches several nodes is shown as unclear, never guessed.",
  talkers:
    "The nodes that sent the most packets and the most adverts in the last 24 hours or 7 days. Each packet counts once, however many observers heard it.",
  neighbour_graph:
    "Every MeshCore node as a dot and every direct radio link we saw as a line, in the last 24 hours or 7 days. Spread by how the nodes link together, or placed on the map.",
  reachability:
    "Per node: how many observers (MeshCore) or feeders (Meshtastic) heard it, and its single furthest confirmed reach. A reach seen only once is marked questionable rather than proven, since a one-off long-distance hit is probably tropospheric ducting, not real coverage, and must never be read as established.",
  regions: REGIONS_TAB_NOTE,
};


// Ask-citation deep link (`#/audit?detector=<id>`, router.ts — MUST keep
// working) routing: which tab a detector's findings surface on, and which
// protocol side to switch to first.
// Static rather than derived from the live /api/audit/detectors response
// on purpose: the citation must route correctly on the very first render,
// before that fetch can possibly have completed.
export const AUDIT_DETECTOR_TAB: Record<string, AuditTabId> = {
  d1_advert_rate: "audit",
  d2_boot_loop: "audit",
  d3_loop_detect: "audit",
  d4_path_hash_width: "audit",
  d5_prefix_collision: "audit",
  d8_position_plausibility: "location",
};


export const AUDIT_DETECTOR_PROTOCOL: Record<string, Protocol | null> = {
  d1_advert_rate: "meshcore",
  d2_boot_loop: "meshcore",
  d3_loop_detect: "meshcore",
  d4_path_hash_width: "meshcore",
  d5_prefix_collision: "meshcore",
  d8_position_plausibility: null,
};


// --- Behaviour tab "More filters" (numeric evidence bounds) ---------------
// Plain field labels, defined once here rather than inline in
// BehaviourTab.tsx (2026-09-25 audit plain-wording task) — same "one
// registry, frontend just renders it" discipline as detector label/
// meaning/reason text (those are backend-authoritative since they also
// drive API-independent strings like level_reason; these are pure
// frontend display strings for a frontend-only URL param shape
// (`ev_<field>_min`/`ev_<field>_max`, routes/audit.py's own
// `_evidence_bounds_from_request`), so they live here instead).
// width_bytes/observed_max_width_bytes: "Path hash" is MeshCore's own term
// for this (firmware repo docs/cli_commands.md's `path.hash.mode`; see the
// longer citation on pathHashLabel in MeshCoreConfigAudit.tsx) — reverted
// 2026-09-25 from an earlier "ID length" plain-English rename, to
// use the wording from the MeshCore devs instead.
export const AUDIT_EVIDENCE_FIELD_LABEL: Record<string, string> = {
  observed_interval_minutes: "Minutes between adverts",
  sample_intervals: "Adverts sampled",
  member_count: "Nodes sharing the ID",
  width_bytes: "Path hash (bytes)",
  observed_max_width_bytes: "Longest path hash seen (bytes)",
};


// Tooltips for the "More filters" numeric evidence bounds (2026-09-26
// check-fixes batch 1, fix 8) — plain explanations of what each field
// means, confirmed against the owning detector's own evidence dict
// (backend/amalgamesh/audit/detectors/d1_advert_rate.py, d3_loop_detect.py,
// d4_path_hash_width.py, d5_prefix_collision.py). `width_bytes` and
// `observed_max_width_bytes` sound alike but are NOT the same measurement:
// `width_bytes` is the path-hash width actually involved in ONE specific
// D3/D5 finding (the hash width a loop-detect inference or a prefix-
// collision group was observed at); `observed_max_width_bytes` is the
// WIDEST path-hash width a node has EVER been seen using on its own
// adverts, D4's own "best case this node has shown" measurement — their
// own tooltips call this difference out directly rather than leaving two
// similarly-named fields unexplained side by side.
export const AUDIT_EVIDENCE_FIELD_TOOLTIP: Record<string, string> = {
  observed_interval_minutes:
    "How far apart this node's flood adverts arrive, at their most common spacing (the mode of the inter-arrival times, not the average).",
  sample_intervals:
    "How many advert-to-advert gaps went into that measurement — filters to nodes with at least this many (or at most this many) sampled intervals.",
  member_count:
    "The \"short ID\" is a 1, 2, or 3-byte prefix of a node's public key — MeshCore's own compact way of naming a node in a path hash instead of sending the full key. This counts how many known nodes share that exact prefix — the higher this number, the more nodes a path reading that prefix could actually mean.",
  width_bytes:
    "The path-hash width (in bytes) involved in THIS specific finding — how wide a hash the colliding or loop-detect-relevant nodes were sharing at the time, not any one node's overall setting.",
  observed_max_width_bytes:
    "The widest path-hash size this node has ever been seen using on its own adverts (MeshCore's path.hash.mode; devs call 2/3-byte hashes \"Multibyte\") — D4 flags a node whose widest-ever hash is still narrower than this mesh's 2-byte policy.",
};


// Which detector(s) each field's evidence actually comes from (confirmed
// against each detector module's own evidence dict) — used to show only
// the fields relevant to the active protocol's detectors, same rule the
// Check/Role filters already follow.
export const AUDIT_EVIDENCE_FIELD_DETECTORS: Record<string, string[]> = {
  observed_interval_minutes: ["d1_advert_rate"],
  sample_intervals: ["d1_advert_rate"],
  member_count: ["d5_prefix_collision"],
  width_bytes: ["d3_loop_detect", "d5_prefix_collision"],
  observed_max_width_bytes: ["d4_path_hash_width"],
};


// Plain short labels for a finding's own evidence dict, rendered by
// EvidenceList (BehaviourTab.tsx) in an expanded finding row — DISPLAY
// ONLY, a separate list from AUDIT_EVIDENCE_FIELD_LABEL above (that one is
// scoped tightly to the numeric fields the backend's ev_<field>_min/max
// filter bounds actually accept; most of these new D1/D2/D4/D5 fields
// aren't filterable, just worth a human label instead of a raw evidence
// key when shown). Added 2026-09-25 for the D1/D2/D4/D5 check-fixes
// rebuild's own new evidence keys (backend/amalgamesh/audit/detectors/
// d1_advert_rate.py, d2_boot_loop.py, d4_path_hash_width.py,
// d5_prefix_collision.py) — a key with no entry here still renders fine
// (EvidenceList falls back to the raw key name), this only upgrades the
// ones worth a plain-English gloss.
export const AUDIT_EVIDENCE_KEY_LABEL: Record<string, string> = {
  // D1 — advert rate
  interval_hours: "Advert interval (hours)",
  color: "Traffic-light colour",
  min_intervals_required: "Minimum samples needed",
  mode_bucket_sample_count: "Samples in the modal bucket",
  firmware_min_interval_hours: "Firmware minimum (hours)",
  policy_floor_hours: "Mesh policy floor (hours)",
  firmware_new_install_default_hours: "Firmware new-install default (hours)",
  advert_airtime_cost_s_per_day: "Advert airtime cost (s/day)",
  advert_airtime_sample_count: "Advert airtime samples",
  // D2 — boot loop / clock reset
  clock_resets: "Confirmed firmware reset signatures",
  reset_events: "Confirmed reset event details",
  clock_backward_facts: "Other backward clock moves (unconfirmed)",
  clock_backward_fact_events: "Unconfirmed backward clock event details",
  clusters: "Advert clusters",
  events_total: "Adverts in window",
  zero_hop_events_included: "Zero-hop adverts included",
  cluster_window_minutes: "Cluster window (minutes)",
  // D4 — path hash width
  neighbor_set_size: "Nodes in neighborhood",
  collision_probability_1byte: "Estimated collision chance (1-byte ID)",
  // D5 — prefix collision
  adjacency_confirmed: "Shared relay confirmed",
  best_common_neighbor_count: "Shared relay count",
  best_pair: "Best-evidenced pair",
  nearest_pair_km: "Nearest pair distance (km)",
};


// Observer-disagreement fields worth surfacing under Audit > Reachability
// (moved there from the retired Coverage destination). On the un-filtered
// list, all 270 current entries were last_heard
// differing by minutes between observers, which is polling latency, not
// disagreement. Only genuine conflicts — position, role, name — are worth
// showing. `last_heard` (and anything else not in this list) is excluded
// as timing noise rather than shown as a false "disagreement" — confirmed
// live, 2026-09-20: 243 of 255 raw entries in a 24h window were exactly
// this noise. "role" hasn't been observed live yet but is kept in the
// list rather than dropped for not having shown up yet.
export const GENUINE_DISAGREEMENT_FIELDS: string[] = ["name", "role", "position"];


// --- Reachability confidence (GET /api/audit/reachability) -----------------
// Design rule: reachability needs a proven-or-questionable state — if it
// hits the same reach over and over again it's proven, but if it happens
// once it's probably tropospheric ducting. Confidence must be visually
// unmistakable — a "questionable" reach is probably not real coverage, and
// must never read as established. The label itself says so (not just the
// colour) so it survives a glance, a screenshot, or a colourblind viewer.
export type ReachabilityConfidence = "proven" | "questionable";


export interface ReachabilityConfidenceInfo {
  label: string;
  color: { light: string; dark: string };
}


export const REACHABILITY_CONFIDENCE_INFO: Record<ReachabilityConfidence, ReachabilityConfidenceInfo> = {
  proven: { label: "Proven", color: STATUS.good },
  questionable: { label: "Questionable — possible ducting", color: STATUS.warning },
};


export function isReachabilityConfidence(x: string | null | undefined): x is ReachabilityConfidence {
  return x === "proven" || x === "questionable";
}


export function reachabilityConfidenceInfo(raw: string | null | undefined): ReachabilityConfidenceInfo {
  return isReachabilityConfidence(raw) ? REACHABILITY_CONFIDENCE_INFO[raw] : { label: raw || "Unknown", color: CHROME.inkMuted };
}


// Mirrors api/types.ts's own ReachabilityPositionStatus — kept as a
// separate local definition rather than an import, same convention as
// AuditFinding.verdict there being plain `string` while AuditVerdict lives
// here: this file and api/types.ts are both independent leaf modules (a
// backend response type never imports from the vocabulary file, or vice
// versa), so a shared enum-like spelling is declared once per side, each
// independently guarded against an unrecognised value.
type ReachabilityPositionStatus = "valid" | "unset" | "near_null_island" | "implausible_distance" | "sign_error_suspected";


// position_status labels — "unset" carries no position_note (nothing to
// explain: no position was ever reported), so it gets its own plain-
// English sentence here rather than showing nothing. Every other
// non-"valid" status carries a real position_note from the backend,
// used verbatim instead of this fallback — see reachabilityPositionNote().
export const REACHABILITY_POSITION_STATUS_LABEL: Record<ReachabilityPositionStatus, string> = {
  valid: "Valid",
  unset: "No position reported",
  near_null_island: "Near Null Island (0,0)",
  implausible_distance: "Implausible distance",
  sign_error_suspected: "Sign error suspected",
};


function isReachabilityPositionStatus(x: string | null | undefined): x is ReachabilityPositionStatus {
  return x === "valid" || x === "unset" || x === "near_null_island" || x === "implausible_distance" || x === "sign_error_suspected";
}


// The honest reason a furthest reach couldn't be computed for this node —
// show the reason rather than a blank, so nobody concludes the node simply
// has no position. Returns null only for "valid" (nothing to
// explain there; a valid-position node with no computable reach gets its
// own separate "insufficient data" message at the call site, since that's
// a different fact than a bad position).
export function reachabilityPositionNote(status: string | null | undefined, note: string | null | undefined): string | null {
  if (!isReachabilityPositionStatus(status) || status === "valid") return null;
  if (note) return note;
  return status === "unset" ? "No position has ever been reported for this node." : REACHABILITY_POSITION_STATUS_LABEL[status];
}


// Shared window vocabulary for the Audit pages: 1h to 7d (7 days is the longest live window; older
// results are the weekly History snapshots).
export type AuditWindow = "1h" | "6h" | "12h" | "24h" | "7d";


export const AUDIT_WINDOW_ORDER: AuditWindow[] = ["1h", "6h", "12h", "24h", "7d"];


// Live pages that are not part of Audit (Ask, Hazards events) still offer a 30-day span.
export type LiveWindow = AuditWindow | "30d";


export const LIVE_WINDOW_ORDER: LiveWindow[] = ["1h", "6h", "12h", "24h", "7d", "30d"];

// 2026-09-30 (Stage B, "always cached" rework): every Analysis report is
// now precomputed on a timer and read from a durable store — an endpoint
// never computes on the request path. The ONE case with nothing to read
// yet is a brand-new install before the background pass has completed
// even once; every such tab shows this SAME message (mirrored verbatim in
// the backend's own audit/snapshot_serving.py `FIRST_CALCULATION_TEXT`)
// rather than a generic spinner, so it reads as "this resolves on its
// own shortly", not "something is stuck".
export const FIRST_CALCULATION_TEXT = "First calculation in progress — check back in a few minutes.";


// --- Node page: Audit section ------------------------------------------------
export const NODE_AUDIT_SECTION_TITLE = "Audit";

export const NODE_AUDIT_SECTION_DESCRIPTION = "How this node looks in the last 7 days, and anything the audit checks have found about it.";

export const NODE_AUDIT_NO_ROW_TEXT = "No audit row for this node yet.";

export const NODE_AUDIT_NO_FINDINGS_TEXT = "No findings for this node in the last 7 days.";

export const NODE_AUDIT_FINDINGS_HEADING = "Findings";

export const NODE_AUDIT_VALUES_HEADING = "Current values";

export const NODE_AUDIT_UNAVAILABLE_TEXT = "Not available from this backend right now.";


export function nodeAuditFindingLastSeen(lastSeenText: string): string {
  return `last seen ${lastSeenText}`;
}
